/**
 * Shared CV completion calculator
 * Used by ProfileSidebar, CVChat banner, and other components
 */

const SECTIONS = [
  { key: 'identity', weight: 10 },
  { key: 'contact', weight: 10 },
  { key: 'bio', weight: 10 },
  { key: 'target_job', weight: 5 },
  { key: 'education', weight: 15 },
  { key: 'experience', weight: 20 },
  { key: 'skills', weight: 10 },
  { key: 'certifications', weight: 5 },
  { key: 'projects', weight: 5 },
  { key: 'languages', weight: 5 },
  { key: 'awards', weight: 5 },
];

export const calculateSectionQuality = (profile, key) => {
  if (!profile) return 0;
  
  switch (key) {
    case 'identity':
      if (profile.first_name && profile.last_name) return 100;
      if (profile.first_name || profile.last_name) return 50;
      return 0;
    case 'contact':
      if (profile.contact?.email || profile.email) return 100;
      return 0;
    case 'bio':
      if (!profile.bio) return 0;
      if (profile.bio.length > 150) return 100;
      if (profile.bio.length > 80) return 75;
      if (profile.bio.length > 30) return 50;
      return 25;
    case 'target_job':
      if (!profile.target_job) return 0;
      return profile.target_job.length > 5 ? 100 : 50;
    case 'education':
      if (!profile.education?.length) return 0;
      let eduScore = 0;
      profile.education.forEach(edu => {
        let s = 0;
        if (edu.degree) s += 40;
        if (edu.institution) s += 40;
        if (edu.end_date) s += 20;
        eduScore = Math.max(eduScore, s);
      });
      return eduScore;
    case 'experience':
      if (!profile.experience?.length) return 0;
      let expScore = 0;
      profile.experience.forEach(exp => {
        let s = 0;
        if (exp.position) s += 25;
        if (exp.company) s += 25;
        if (exp.description && exp.description.length > 30) s += 30;
        if (exp.responsibilities?.length > 0) s += 20;
        expScore = Math.max(expScore, s);
      });
      return expScore;
    case 'skills':
      if (!profile.skills?.length) return 0;
      if (profile.skills.length >= 6) return 100;
      if (profile.skills.length >= 4) return 75;
      if (profile.skills.length >= 2) return 50;
      return 25;
    case 'certifications':
      if (!profile.certifications?.length) return 0;
      return profile.certifications.length >= 2 ? 100 : 70;
    case 'projects':
      if (!profile.projects?.length) return 0;
      return profile.projects.length >= 2 ? 100 : 70;
    case 'languages':
      if (!profile.languages?.length) return 0;
      let langScore = 0;
      profile.languages.forEach(lang => {
        if (lang.name && lang.level) langScore += 50;
        else if (lang.name) langScore += 25;
      });
      return Math.min(100, langScore);
    case 'awards':
      if (!profile.awards?.length) return 0;
      return profile.awards.length >= 1 ? 100 : 50;
    default:
      return 0;
  }
};

/**
 * Calculate overall CV completion percentage
 * Returns { completion, sectionQualities }
 */
export const calculateCompletion = (profile) => {
  if (!profile) return { completion: 0, sectionQualities: {} };

  const sectionQualities = {};
  let totalScore = 0;
  let totalWeight = 0;

  SECTIONS.forEach(({ key, weight }) => {
    const quality = calculateSectionQuality(profile, key);
    sectionQualities[key] = quality;
    totalScore += (quality / 100) * weight;
    totalWeight += weight;
  });

  const completion = Math.round((totalScore / totalWeight) * 100);
  return { completion, sectionQualities };
};

/**
 * Get incomplete sections with labels for suggestion chips
 */
export const getIncompleteSections = (profile, language = 'en') => {
  if (!profile) return [];

  const { sectionQualities } = calculateCompletion(profile);
  
  const sectionLabels = language === 'en' ? {
    contact: { emoji: '📧', label: 'Add contact info' },
    experience: { emoji: '💼', label: 'Add work experience details' },
    education: { emoji: '🎓', label: 'Complete education' },
    certifications: { emoji: '📜', label: 'Add certifications' },
    skills: { emoji: '💡', label: 'Add more skills' },
    bio: { emoji: '📝', label: 'Write a professional summary' },
    languages: { emoji: '🌍', label: 'Add languages' },
    target_job: { emoji: '🎯', label: 'Set your target job' },
    identity: { emoji: '👤', label: 'Complete your name' },
    projects: { emoji: '🔧', label: 'Add projects' },
    awards: { emoji: '🏆', label: 'Add awards' },
  } : {
    contact: { emoji: '📧', label: 'Ajouter mes coordonnées' },
    experience: { emoji: '💼', label: 'Ajouter mon expérience professionnelle' },
    education: { emoji: '🎓', label: 'Compléter ma formation' },
    certifications: { emoji: '📜', label: 'Ajouter des certifications' },
    skills: { emoji: '💡', label: 'Ajouter plus de compétences' },
    bio: { emoji: '📝', label: 'Écrire un résumé professionnel' },
    languages: { emoji: '🌍', label: 'Ajouter des langues' },
    target_job: { emoji: '🎯', label: 'Définir mon objectif professionnel' },
    identity: { emoji: '👤', label: 'Compléter mon nom' },
    projects: { emoji: '🔧', label: 'Ajouter des projets' },
    awards: { emoji: '🏆', label: 'Ajouter des récompenses' },
  };

  // Sort by weight (most impactful first), filter incomplete
  const incomplete = SECTIONS
    .filter(({ key }) => sectionQualities[key] < 100)
    .sort((a, b) => b.weight - a.weight)
    .map(({ key }) => ({
      key,
      quality: sectionQualities[key],
      ...sectionLabels[key],
      message: language === 'en' 
        ? `I want to improve my ${key === 'target_job' ? 'objective' : key} section`
        : `Je veux améliorer ma section ${key === 'target_job' ? 'objectif' : key === 'bio' ? 'bio' : key === 'identity' ? 'identité' : key}`,
    }));

  return incomplete;
};
